import type { AxiosError } from 'axios';
import axios, { type InternalAxiosRequestConfig } from 'axios';

import i18next from '@/i18n';
import { ROUTES } from '@/shared/routes';
import { abortControllerManager } from '@/shared/services/helpers/api';
import { getAppNavigate } from '@/shared/services/helpers/navigate-helper';
import {
  getUserToken,
  removeUserToken,
  setUserToken
} from '@/shared/services/user-hash';

// Расширяем InternalAxiosRequestConfig для хранения AbortController
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __abortController?: AbortController;
}

export type CustomAxiosError = AxiosError<{
  message?: string;
  statusCode: number;
}>;

let isRefreshing = false;

// Создаем axios instance для API
export const axiosInstance = axios.create({
  baseURL: process.env.PUBLIC_API_URL
});

// Request interceptor: автоматически создает AbortController для каждого запроса
axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = getUserToken().accessToken;
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Если signal уже передан явно, используем его
  if (!config.signal) {
    const controller = abortControllerManager.createController();
    config.signal = controller.signal;
    // Сохраняем controller в config для последующего удаления
    (config as ExtendedAxiosRequestConfig).__abortController = controller;
  }
  return config;
});

// Response interceptor: удаляет AbortController после завершения запроса
axiosInstance.interceptors.response.use(
  (response) => {
    const controller = (response.config as ExtendedAxiosRequestConfig)
      .__abortController;
    if (controller) {
      abortControllerManager.removeController(controller);
    }
    return response;
  },
  async (error) => {
    const controller = (error.config as ExtendedAxiosRequestConfig)
      ?.__abortController;
    if (controller) {
      abortControllerManager.removeController(controller);
    }

    try {
      const originalRequest = error.config as InternalAxiosRequestConfig;

      if (error.response?.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;

          const refreshToken = getUserToken().refreshToken;
          const newResponse = await axios<{
            accessToken: string;
            refreshToken: string;
          }>({
            url: `${process.env.PUBLIC_API_URL}/auth/refresh`,
            method: 'get',
            withCredentials: false,
            headers: {
              'Access-Control-Allow-Origin': '*',
              Authorization: `Bearer ${refreshToken}`
            }
          });
          if (
            newResponse.data.accessToken &&
            newResponse.data.accessToken !== 'undefined'
          ) {
            setUserToken({
              accessToken: newResponse.data.accessToken,
              refreshToken: newResponse.data.refreshToken
            });
            originalRequest.headers.Authorization = `Bearer ${newResponse.data.accessToken}`;
            isRefreshing = false;
            return axiosInstance(originalRequest);
          }
        }
      }
      if (!axios.isCancel(error)) throw error;
      return Promise.reject(error);
    } catch (e) {
      const error = e as CustomAxiosError;
      isRefreshing = false;

      if (error.response?.status === 401) {
        const navigate = getAppNavigate();
        removeUserToken();
        const newPath = `/${ROUTES.login}`;
        const savedUrl =
          window.location.pathname !== `/${i18next.language}${newPath}` &&
          !window.location.pathname.includes('create')
            ? `${window.location.pathname}${window.location.search}`
            : '';
        navigate(newPath, { state: { savedUrl } });
      }

      return Promise.reject(error);
    }
  }
);
